import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { GameSnapshot } from './socket';
import { API_URL } from './api';
import { serverNow } from './time-sync';

// Broadcast lower-third overlay. Renders a small, transparent-background
// bar showing team scores + clock + period, sized + positioned for OBS
// browser sources. Subscribes to the game socket using an overlay token
// instead of a display token.

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

function readToken(): string | null {
  const m = window.location.pathname.match(/^\/overlay\/([^/]+)/);
  return m?.[1] ?? null;
}

function readPosition(): Position {
  const p = new URLSearchParams(window.location.search).get('pos');
  if (p === 'top-left' || p === 'top-right' || p === 'bottom-right') return p;
  return 'bottom-left';
}

function readScale(): number {
  const s = new URLSearchParams(window.location.search).get('scale');
  const n = s ? parseFloat(s) : 1;
  return Number.isFinite(n) && n > 0.4 && n < 3 ? n : 1;
}

function fmtClock(m: number, s: number): string {
  return `${m}:${String(s).padStart(2, '0')}`;
}

function useLiveClock(snap: GameSnapshot | null): { m: number; s: number } {
  const [, force] = useState(0);
  useEffect(() => {
    if (!snap?.state.clock.running) return;
    const id = setInterval(() => force((x) => x + 1), 500);
    return () => clearInterval(id);
  }, [snap?.state.clock.running, snap?.state.clock.startedAt]);
  if (!snap) return { m: 0, s: 0 };
  const c = snap.state.clock;
  if (!c.running || !c.startedAt) return { m: c.m, s: c.s };
  const elapsed = Math.max(
    0,
    Math.floor((serverNow() - new Date(c.startedAt).getTime()) / 1000),
  );
  const total = Math.max(0, c.m * 60 + c.s - elapsed);
  return { m: Math.floor(total / 60), s: total % 60 };
}

export function OverlayScreen() {
  const token = readToken();
  const position = readPosition();
  const scale = readScale();
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Make body transparent for OBS browser source.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setError('Missing overlay token in URL.');
      return;
    }

    // 1) Fetch initial snapshot via the public REST endpoint.
    fetch(`/api/games/overlay/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((s: GameSnapshot) => setSnapshot(s))
      .catch(() => setError('Invalid or revoked overlay token.'));

    // 2) Subscribe to the live socket for updates.
    const socket = io(API_URL || window.location.origin, {
      auth: { overlayToken: token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    socketRef.current = socket;
    socket.on('game:state', (msg: GameSnapshot) => setSnapshot(msg));
    socket.on('connect_error', () => {});
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  const clock = useLiveClock(snapshot);

  if (error) {
    return (
      <div
        style={{
          padding: 20,
          color: '#fff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: 'rgba(0,0,0,0.7)',
        }}
      >
        {error}
      </div>
    );
  }

  if (!snapshot) {
    return <div style={{ background: 'transparent' }} />;
  }

  const home = snapshot.rosters.home;
  const away = snapshot.rosters.away;
  const homeColor = home.colorPrimary ?? '#dc2626';
  const awayColor = away.colorPrimary ?? '#1d4ed8';
  const homeScore = snapshot.state.home.score;
  const awayScore = snapshot.state.away.score;
  const period = snapshot.state.period;
  const isHalftime = snapshot.state.intermission === 'halftime';

  const posStyle: React.CSSProperties = {
    position: 'fixed',
    transform: `scale(${scale})`,
    transformOrigin: position.includes('right') ? 'right' : 'left',
    ...(position.startsWith('top') ? { top: 20 } : { bottom: 20 }),
    ...(position.endsWith('right') ? { right: 20 } : { left: 20 }),
  };

  return (
    <div
      style={{
        margin: 0,
        background: 'transparent',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <div style={posStyle}>
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            minWidth: 560,
            background: 'rgba(15, 18, 26, 0.92)',
            backdropFilter: 'blur(8px)',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            fontFamily:
              "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <SideBug
            color={homeColor}
            abbr={home.abbreviation ?? home.name.slice(0, 3).toUpperCase()}
            name={home.name}
            score={homeScore}
            alignLeft
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px 18px',
              gap: 2,
              background: 'rgba(255,255,255,0.04)',
              borderLeft: '1px solid rgba(255,255,255,0.06)',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              minWidth: 120,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 3,
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              {isHalftime ? 'HALFTIME' : `Q${period}`}
            </div>
            <div
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: 1,
                color: snapshot.state.clock.running ? '#22c55e' : '#fff',
                lineHeight: 1,
              }}
            >
              {fmtClock(clock.m, clock.s)}
            </div>
          </div>
          <SideBug
            color={awayColor}
            abbr={away.abbreviation ?? away.name.slice(0, 3).toUpperCase()}
            name={away.name}
            score={awayScore}
          />
        </div>
      </div>
    </div>
  );
}

function SideBug({
  color,
  abbr,
  name,
  score,
  alignLeft,
}: {
  color: string;
  abbr: string;
  name: string;
  score: number;
  alignLeft?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 22px',
        flex: 1,
        flexDirection: alignLeft ? 'row' : 'row-reverse',
      }}
    >
      <div
        style={{
          width: 6,
          alignSelf: 'stretch',
          background: color,
          borderRadius: 2,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ...(alignLeft ? {} : { alignItems: 'flex-end' }),
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 2,
            color: 'rgba(255,255,255,0.55)',
          }}
        >
          {abbr}
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            lineHeight: 1,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={name}
        >
          {name}
        </div>
      </div>
      <div
        style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 40,
          fontWeight: 800,
          marginLeft: alignLeft ? 'auto' : 0,
          marginRight: alignLeft ? 0 : 'auto',
          lineHeight: 1,
        }}
      >
        {score}
      </div>
    </div>
  );
}
